import type { CamForecastComparison, CamForecastTopMatch } from 'types/camera';
import { oneMinuteSeconds } from 'utils/time';

/** Max seconds between scrubbed graph time and nearest forecast comparison bucket. */
export const MAX_FORECAST_MATCH_OFFSET_SECONDS = 30 * oneMinuteSeconds;

export type ForecastComparisonMeta = Pick<CamForecastComparison, 'datetime' | 'utcOffset'>;

export interface PickClosestForecastComparisonResult {
  matches: readonly CamForecastTopMatch[];
  forecastComparison: ForecastComparisonMeta | null;
}

const pickMeta = (c: CamForecastComparison): ForecastComparisonMeta => ({
  datetime: c.datetime,
  utcOffset: c.utcOffset,
});

/**
 * Selects the forecast comparison bucket closest to a graph scrub timestamp.
 * Both `timestampSeconds` and comparison `datetime` are real UTC Unix seconds.
 */
export function pickClosestForecastComparison(
  comparisons: readonly CamForecastComparison[],
  timestampSeconds: number | null | undefined,
): PickClosestForecastComparisonResult {
  const empty: PickClosestForecastComparisonResult = {
    matches: [],
    forecastComparison: null,
  };

  if (!comparisons.length) {
    return empty;
  }

  if (timestampSeconds == null) {
    const first = comparisons[0];
    return {
      matches: first.topMatches ?? [],
      forecastComparison: pickMeta(first),
    };
  }

  const closest = comparisons.reduce((best, c) =>
    Math.abs(c.datetime - timestampSeconds) < Math.abs(best.datetime - timestampSeconds) ? c : best,
  );

  if (Math.abs(closest.datetime - timestampSeconds) > MAX_FORECAST_MATCH_OFFSET_SECONDS) {
    return empty;
  }

  return {
    matches: closest.topMatches ?? [],
    forecastComparison: pickMeta(closest),
  };
}
